// Cash Handling presentation — the locked labels and the two pure functions the
// section renders from. `05 Rules/Cash Handling Rules` (approved, prefix CASH).
//
// ⚠ ITS OWN FILE FOR THE REASON `cash.custody.ts` IS: these are pure functions
// over a row, and `cash.hooks.ts` imports the Supabase client and the Edge
// caller. Sitting next to those made the label untestable — the admin vitest
// setup does not resolve the app's `@/` alias, so importing it dragged in a
// module the test runner could not load.

/**
 * ⚠ LOCKED COPY. CASH: "The following confirmation labels are locked and must be
 * used exactly as written." The partner app pins the same strings in
 * `apps/partner/src/features/common/cashHandling.ts`; both exist because the
 * same handoff is confirmed from two different apps, and a hub manager reading
 * "Mark received" for an action the rider's phone calls "Confirm Cash Collected"
 * is two people describing one event differently.
 */
export const CASH_HANDLING_LABELS = {
  payerAmount: "Cash to Pay",
  payerConfirm: "Confirm Cash Payment",
  recipientAmount: "Cash to Collect",
  recipientConfirm: "Confirm Cash Collected",
  pending: "Pending Confirmation",
  collected: "Cash Collected",
  issue: "Report an Issue",
} as const;

export type HandoffStageBadge = {
  label: string;
  variant: "muted" | "warning" | "success" | "destructive";
};

/**
 * The stage, in CASH's own words rather than the enum's.
 *
 * `awaiting_payment` has no approved label of its own — the page names the two
 * confirmations and the two states between them — so it is described from the
 * payer's outstanding action rather than invented as a status noun.
 */
export function handoffStageBadge(status: string): HandoffStageBadge {
  switch (status) {
    case "pending_confirmation":
      return { label: CASH_HANDLING_LABELS.pending, variant: "warning" };
    case "cash_collected":
      return { label: CASH_HANDLING_LABELS.collected, variant: "success" };
    case "issue_reported":
      return { label: "Issue reported", variant: "destructive" };
    default:
      return { label: "Awaiting payment", variant: "muted" };
  }
}

/**
 * Who a party is, on a screen that shows all three legs.
 *
 * "the hub" is a place and has no staff row (0179: `recipient_staff_id` is null
 * exactly when the recipient is the Hub), so it is named by the hub itself.
 */
export function handoffPartyLabel(
  kind: string,
  staffName: string | null,
  hubName: string | null,
): string {
  if (kind === "hub") return hubName ?? "the hub";
  if (staffName) return staffName;
  return kind === "rider" ? "the rider" : "the therapist";
}
